import { of as observableOf, Observable, of } from 'rxjs';

import { map, zip, flatMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { DataService, ApiService, InspectionPluginService } from '../../shared/services/index';
import { InspectionPlugin, Icons } from '../../shared/models/index';
import { PartsPluginFactoryService } from './parts-plugin-factory.service';

@Injectable()
export class PartsPluginService extends InspectionPluginService {
  private importedParts: any;

  constructor(protected apiService: ApiService, protected dataService: DataService, private partsFactory: PartsPluginFactoryService) {
    super(apiService, dataService, null, partsFactory);
  }

  public updateParts(inspectionId: string, pointId: string, pluginId: string, parts: any, points: any): Observable<any> {
    return this._updateParts(inspectionId, pointId, pluginId, parts, points).pipe(
      map((res: InspectionPlugin) => {
        if (pointId) {
          this.dataService.processIcons(points.NeedParts, Icons.getIconByName(Icons.requiredPartsIconName), inspectionId, pointId);
          this.dataService.processIcons(parts && parts.length > 0, Icons.getIconByName(Icons.hasPartsIconName), inspectionId, pointId);
        }
        return this.dataService.updatePlugin(inspectionId, res);
      })
    );
  }

  public updatePart(inspectionId: string, pointId: string, pluginId: string, oldPart: any, part: any): Observable<any> {
    return this._updatePart(inspectionId, pointId, pluginId, oldPart, part).pipe(
      map((res: InspectionPlugin) => {
        return this.dataService.updatePlugin(inspectionId, res);
      })
    );
  }

  public addParts(inspectionId: string, pointId: string, pluginId: string, parts: any, isImport?: boolean): Observable<any> {
    return this._addParts(inspectionId, pointId, pluginId, parts, isImport).pipe(
      map((res: InspectionPlugin) => {
        return this.dataService.updatePlugin(inspectionId, res);
      })
    );
  }

  public getPlugin(inspectionId: string, pluginId: string): Observable<InspectionPlugin> {
    return this.dataService.getPartsPlugin(inspectionId);
  }

  public connectToPartview(inspectionId: string, pointId: string, pluginId: string, equipmentNumber: string, catalog = '') {
    let url = `/inspections/${inspectionId}/points/${pointId}/parts/${pluginId}/connect/${equipmentNumber}/catalogs/${catalog}`;
    if (!catalog) url = `/inspections/${inspectionId}/points/${pointId}/parts/${pluginId}/connect/${equipmentNumber}`;
    this.apiService.get(url).subscribe((data) => {
      window.open(data.url, '_blank');
    });
  }

  public setImportedParts(parts: any) {
    this.importedParts = parts;
  }

  public getImportedParts() {
    return observableOf(this.importedParts);
  }

  public deletePlugin(inspectionId: string, pointId: string, pluginId: string): any {
    return this._deleteParts(inspectionId, pointId, pluginId).pipe(
      zip(super.deletePlugin(inspectionId, pointId, pluginId)),
      map(([plugin, success]) => {
        this.dataService.updatePlugin(inspectionId, plugin);
        this.dataService.processIcons(false, Icons.getIconByName(Icons.requiredPartsIconName), inspectionId, pointId);
        this.dataService.processIcons(false, Icons.getIconByName(Icons.hasPartsIconName), inspectionId, pointId);
        return success;
      })
    );
  }

  public deletePart(inspectionId: string, pointId: string, pluginId: string, part: any): any {
    return this._deletePart(inspectionId, pointId, pluginId, part).pipe(
      map((plugin) => {
        this.dataService.updatePlugin(inspectionId, plugin);
        return plugin;
      })
    );
  }

  public updateIcons(inspectionId: string, pointId: string, parts: any) {
    this.dataService.processIcons(parts && parts.length > 0, Icons.getIconByName(Icons.hasPartsIconName), inspectionId, pointId);
  }

  public getCatalogs(inspectionId: string, pointId: string, pluginId: string, equipmentId: string): Observable<any[]> {
    const url = `/inspections/${inspectionId}/points/${pointId}/parts/${pluginId}/equipments/${equipmentId}`;
    return this.apiService.get(url);
  }

  private _updateParts(inspectionId: string, pointId: string, pluginId: string, parts: any, points: any): Observable<any> {
    return this.partsFactory.updateParts(inspectionId, pointId, pluginId, parts, points).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(plugin.postServerUrl.replace('/null/', `/${pointId}/`), plugin.config);
      })
    );
  }

  private _updatePart(inspectionId: string, pointId: string, pluginId: string, oldPart: any, part: any): Observable<any> {
    return this.partsFactory.updatePart(inspectionId, pointId, pluginId, oldPart, part).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(plugin.postServerUrl.replace('/null/', `/${pointId}/`), plugin.config);
      })
    );
  }

  private _addParts(inspectionId: string, pointId: string, pluginId: string, parts: any, isImport?: boolean): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/parts/${pluginId}/import/addPart`;
    return this.partsFactory.addParts(inspectionId, pointId, pluginId, parts).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(isImport ? url : plugin.postServerUrl.replace('/null/', `/${pointId}/`), plugin.config);
      })
    );
  }

  private _deleteParts(inspectionId: string, pointId: string, pluginId: string): Observable<any> {
    return this.partsFactory.deleteParts(inspectionId, pointId, pluginId).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(plugin.postServerUrl, plugin.config);
      })
    );
  }

  public _deletePart(inspectionId: string, pointId: string, pluginId: string, part: any): Observable<any> {
    return this.partsFactory.deletePart(inspectionId, part).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(plugin.postServerUrl, plugin.config);
      })
    );
  }
}
